import {
	Input,
	type MantineTransition,
	Select,
	type SelectProps,
	Skeleton,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconBuildingStore } from '@tabler/icons-react';
import { useMemo } from 'react';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { useBranchOptions } from '../../util/api/commonQueries';
import type { SelectableBranchDto } from '../../util/types/SelectableBranchDto';

interface FormBranchSelectorProps<T> extends Omit<SelectProps, 'form'> {
	form: UseFormReturnType<T, (values: T) => T>;
	property: string;
	forFilter?: boolean;
	placeHolder?: string;
	mapJustLabel?: boolean;
	hideArea?: boolean;
	clearable?: boolean;
}

export function FormBranchSelector<T>({
	form,
	property,
	forFilter = false,
	disabled = false,
	placeHolder = '',
	mapJustLabel = false,
	clearable = true,
	hideArea = false,
	searchable = false,
	...selectProps
}: FormBranchSelectorProps<T>) {
	const pharmaciesQuery = useBranchOptions();

	const placeHolderMessage = useMemo(() => {
		if (placeHolder !== '') {
			return placeHolder;
		}

		return 'Select branch associated with target...';
	}, [placeHolder]);

	const selectorProps = {
		label: forFilter ? '' : 'Branch',
		data: pharmaciesQuery.data?.value
			? mapToData(pharmaciesQuery.data.value, mapJustLabel, hideArea)
			: undefined,
		comboboxProps: {
			transitionProps: {
				transition: 'pop' as MantineTransition,
				duration: 200,
			},
			'data-cy': `${property}-list`,
		},
		...form.getInputProps(property),
		'data-cy': `${property}-input`,
		leftSection: <IconBuildingStore {...defaultIconStyle} />,
	};

	return (
		<>
			{!pharmaciesQuery.isLoading ? (
				<Select
					{...selectProps}
					{...selectorProps}
					key={form.key(property)}
					placeholder={placeHolderMessage}
					clearable={clearable}
				/>
			) : (
				<Input.Wrapper label={forFilter ? '' : 'Branch'}>
					<Skeleton height={8} mt={6} width="100%" radius={'xl'} h={25} />
				</Input.Wrapper>
			)}
		</>
	);
}

function mapToData(
	branches: SelectableBranchDto,
	mapJustLabel: boolean,
	hideArea = true,
) {
	return branches.branches.flatMap((p) =>
		!mapJustLabel
			? {
					label: hideArea ? p.name : `${p.area} - ${p.name}`,
					value: p.id.toString(),
				}
			: hideArea
				? p.name
				: `${p.area} - ${p.name}`,
	);
}
